<template>
    <div class="[ flex flex-col gap-4 ]" v-bind="$attrs">
        <div class="[ flex justify-between items-center gap-4 ]">
            <div class="[ flex-1 flex ]">
                <h2 class="[ font-bold ]">CRM</h2>
            </div>
            <!-- <ButtonComponent v-if="data?.type === CRMIntegrationType.Custom" outlined label="Test"
                @click="isTestDialogOpen = true" /> -->
        </div>
        <ProgressSpinner v-if="loading" />
        <div v-else>
            <GridForm size="large">
                <GridFormField id="isActive" label="Active" v-if="data">
                    <template #input>
                        <ToggleSwitch v-model="isActive" />
                    </template>
                </GridFormField>
                <GridFormField id="type" label="Type">
                    <template #input>
                        <Select v-model="type" :options="types" optionLabel="label" optionValue="value"
                            placeholder="Select a type" />
                    </template>
                </GridFormField>
            </GridForm>
            <component :is="optionsComponent" v-if="type" v-model="options" size="large" />
            <CrmMappingScriptsEditor size="large" v-if="type !== CRMIntegrationType.Custom"
                v-model:inbound="inboundScript" v-model:outbound="outboundScript" />
            <ButtonComponent label="Save" @click="saveIntegration" :loading="isSaving" />
        </div>
    </div>
    <JBDialog header="Test custom CRM integration" v-model:visible="isTestDialogOpen">
        <ButtonComponent outlined label="Create Customer" @click="onTestCreateCustomerClicked"
            :loading="isTestingCreateCustomer" />
    </JBDialog>
</template>

<script setup lang="ts">
import { Button as ButtonComponent, ProgressSpinner, Select, ToggleSwitch } from "primevue";
import { useApi, useApiLoader } from '@/services/api';
import { CRMIntegrationType } from '@/services/api/sdk';
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import GridForm from "@/components/ui/GridForm.vue";
import GridFormField from "@/components/ui/GridFormField.vue";
import CrmMappingScriptsEditor from "@/components/integrations/CrmMappingScriptsEditor.vue";
import JBDialog from "@/components/ui/modals/JBDialog.vue";

defineOptions({
    inheritAttrs: false,
});

definePage({
    meta: { title: 'CRM', icon: 'refresh' },
});

// REFS

const type = ref<CRMIntegrationType | null>(null);
const options = ref<any>({});
const isActive = ref(true);
const isTestDialogOpen = ref(false);
const inboundScript = ref('');
const outboundScript = ref('');

const types = [
    { label: 'SalesForce', value: CRMIntegrationType.SalesForce },
    { label: 'Custom', value: CRMIntegrationType.Custom },
]

// COMPUTED

const optionsComponent = computed(() => {
    switch (type.value) {
        case CRMIntegrationType.SalesForce:
            return defineAsyncComponent(() => import('@/components/integrations/SalesForceCrmIntegrationOptions.vue'));
        case CRMIntegrationType.Custom:
            return defineAsyncComponent(() => import('@/components/integrations/CustomCrmIntegrationOptions.vue'));
        default: return null;
    }
});

// API

const api = useApi();

const { data, load, loading } = useApiLoader(async () => {
    const res = await api.getCrmIntegrations();
    return res.data ? res.data[0] : null;
}, null);

const { load: saveIntegration, loading: isSaving } = useApiLoader(async () => {
    if (!data.value) {
        await api.createCrmIntegration({
            createCrmIntegrationRequest: {
                type: type.value!,
                options: options.value,
                inboundScript: inboundScript.value,
                outboundScript: outboundScript.value,
            }
        });
    } else {
        await api.updateCrmIntegration({
            integrationId: data.value?.id!,
            updateCrmIntegrationRequest: {
                type: type.value!,
                options: options.value as any,
                isActive: isActive.value,
                inboundScript: inboundScript.value,
                outboundScript: outboundScript.value,
            }
        });
    }
    await load();
}, null);

const { load: testCreateCustomer, loading: isTestingCreateCustomer } = useApiLoader(async () => {
    await api.testCustomCrmIntegrationCreateCustomer({
        requestBody: {

        }
    });
}, null);

watch(data, () => {
    if (data.value) {
        type.value = data.value.type!;
        options.value = data.value.options;
        isActive.value = data.value.isActive!;
        inboundScript.value = data.value.inboundScript!;
        outboundScript.value = data.value.outboundScript!;
    }
});

// FUNCTION

onMounted(async () => {
    await load();
});

async function onTestCreateCustomerClicked() {
    await testCreateCustomer();
}

</script>